// components/PersonalInfoForm.tsx - Version avec sexe et profession obligatoire
import React, {useState, useEffect, useRef, useCallback} from 'react';
import { MapPin, Globe, Calendar, AlertCircle, Check, X, User, Briefcase } from 'lucide-react';
import { AdhesionFormData, ReferrerInfo } from '../../types/adhesion.types';
import useMembership from "../../hooks/useMembership";
import { useGTMTracking } from '../../hooks/useGTMTracking';

// Import des constantes externes
import {
    WORLD_COUNTRIES,
    GABON_GEOGRAPHIC_DATA,
    getDepartmentsByProvince,
    getCitiesByDepartment,
    getCitiesByProvince,
    getDepartmentInfo,
    filterCountries,
    type Country,
    type Province,
    type Department,
    type City
} from '../../constants/gabonGeographicData';

interface PersonalInfoFormProps {
    formData: AdhesionFormData;
    onFormDataChange: (data: Partial<AdhesionFormData>) => void;
    onNext: () => void;
    onPrevious?: () => void; // 🆕 NOUVEAU PROP pour revenir à l'étape précédente
    referrerInfo?: ReferrerInfo | null;
    onReferralCodeChange?: (code: string) => void;
    errors?: Record<string, string>;
    membershipLevel?: 'sympathisant' | 'partial_member' | 'full_member'; // 🆕 NOUVEAU PROP
}

const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({
                                                               formData,
                                                               onFormDataChange,
                                                               onNext,
                                                               onPrevious,
                                                               referrerInfo,
                                                               onReferralCodeChange,
                                                               errors = {},
                                                               membershipLevel = 'full_member' // 🆕 NOUVEAU PARAM
                                                           }) => {
    // États existants...
    const [filteredProvinces, setFilteredProvinces] = useState<Province[]>([]);
    const [filteredDepartments, setFilteredDepartments] = useState<Department[]>([]);
    const [filteredCities, setFilteredCities] = useState<City[]>([]);
    const [nipStatus, setNipStatus] = useState<{
        checking: boolean;
        available: boolean | null;
        message: string;
    }>({
        checking: false,
        available: null,
        message: ''
    });

    const { checkNipAvailability } = useMembership();
    const [nipCheckTimeout, setNipCheckTimeout] = useState<NodeJS.Timeout | null>(null);

    const [countrySearch, setCountrySearch] = useState(formData.country || 'Gabon');
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    const countryInputRef = useRef<HTMLInputElement>(null);

    // Hook GTM
    const {
        trackAdhesionStep,
        trackValidationError,
        trackNipVerification,
        trackReferralValidation,
        startStepTimer,
        endStepTimer
    } = useGTMTracking();

    // Démarrer le timer au montage
    useEffect(() => {
        trackAdhesionStep(1, 'personal_info', 'start');
        startStepTimer('personal_info_form');

        return () => {
            endStepTimer('personal_info_form');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialisation des données géographiques
    useEffect(() => {
        setFilteredCountries(WORLD_COUNTRIES);
        if (formData.country === 'Gabon') {
            setFilteredProvinces(GABON_GEOGRAPHIC_DATA.provinces);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return () => {
            if (nipCheckTimeout) {
                clearTimeout(nipCheckTimeout);
            }
        };
    }, [nipCheckTimeout]);

    // Gestion de l'autocomplétion des pays
    const handleCountrySearch = (value: string) => {
        setCountrySearch(value);
        onFormDataChange({ country: value });

        const filtered = filterCountries(value);
        setFilteredCountries(filtered);
        setShowCountryDropdown(true);
    };

    const checkNipWithDebounce = useCallback(async (nip: string) => {
        if (nipCheckTimeout) {
            clearTimeout(nipCheckTimeout);
        }

        if (!nip || !validateNip(nip)) {
            setNipStatus({
                checking: false,
                available: null,
                message: ''
            });
            return;
        }

        setNipStatus(prev => ({
            ...prev,
            checking: true,
            message: 'Vérification...'
        }));

        const timeout = setTimeout(async () => {
            try {
                const result = await checkNipAvailability(nip);
                setNipStatus({
                    checking: false,
                    available: result.available,
                    message: result.message || ''
                });

                trackNipVerification(validateNip(nip), result.available);

            } catch (error) {
                setNipStatus({
                    checking: false,
                    available: false,
                    message: 'Erreur de vérification'
                });

                trackNipVerification(validateNip(nip), false);
            }
        }, 800);

        setNipCheckTimeout(timeout);
    }, [checkNipAvailability, nipCheckTimeout, trackNipVerification]);

    const selectCountry = (country: Country) => {
        setCountrySearch(country.name);
        onFormDataChange({
            country: country.name,
            province: '',
            department: '',
            city: '',
            customCity: ''
        });
        setShowCountryDropdown(false);

        if (country.name === 'Gabon') {
            setFilteredProvinces(GABON_GEOGRAPHIC_DATA.provinces);
        } else {
            setFilteredProvinces([]);
        }
        setFilteredDepartments([]);
        setFilteredCities([]);
    };

    // Effet pour tracking validation code de parrainage
    useEffect(() => {
        if (formData.referredBy && referrerInfo) {
            trackReferralValidation(formData.referredBy, true, referrerInfo);
        }
    }, [referrerInfo, formData.referredBy, trackReferralValidation]);

    // Filtrer les départements selon la province sélectionnée
    useEffect(() => {
        if (formData.country === 'Gabon' && formData.province) {
            const departments = getDepartmentsByProvince(formData.province);
            setFilteredDepartments(departments);
        } else {
            setFilteredDepartments([]);
        }
        onFormDataChange({ department: '', city: '', customCity: '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.province]);

    // Filtrer les villes selon le département sélectionné
    useEffect(() => {
        if (formData.country === 'Gabon') {
            if (formData.department) {
                const cities = getCitiesByDepartment(formData.department);
                setFilteredCities(cities);
            } else if (formData.province) {
                const cities = getCitiesByProvince(formData.province);
                setFilteredCities(cities);
            } else {
                setFilteredCities([]);
            }
        } else {
            setFilteredCities([]);
        }
        onFormDataChange({ city: '', customCity: '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.department, formData.province]);

    // Fermer la dropdown des pays si on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (countryInputRef.current && !countryInputRef.current.contains(event.target as Node)) {
                setShowCountryDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Validation NIP
    const validateNip = (value: string): boolean => {
        const nipPattern = /^[A-Za-z0-9]{2}-[A-Za-z0-9]{4}-[A-Za-z0-9]{8}$/;
        return nipPattern.test(value);
    };

    const formatNip = (value: string): string => {
        const alphanumeric = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        const limited = alphanumeric.slice(0, 14);

        if (limited.length >= 10) {
            return `${limited.slice(0, 2)}-${limited.slice(2, 6)}-${limited.slice(6)}`;
        } else if (limited.length >= 6) {
            return `${limited.slice(0, 2)}-${limited.slice(2, 6)}-${limited.slice(6)}`;
        } else if (limited.length >= 2) {
            return `${limited.slice(0, 2)}-${limited.slice(2)}`;
        }
        return limited;
    };

    // Validation âge
    const calculateAge = (birthDate: string): number | null => {
        if (!birthDate) return null;
        const birth = new Date(birthDate);
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    // 🆕 FORMATAGE TÉLÉPHONE GABONAIS
    const formatGabonPhone = (value: string, country: string): string => {
        if (country !== 'Gabon') return value;
        
        // Nettoyer : garder seulement les chiffres
        const digits = value.replace(/\D/g, '');
        
        // Formats gabonais reconnus
        if (digits.startsWith('24107') || digits.startsWith('24106')) {
            // Déjà au format international +2410XXXXXXXX → +241XXXXXXXX
            return `+${digits}`;
        }
        
        if (digits.startsWith('2417') || digits.startsWith('2416')) {
            // Format international sans le 0 : +2417XXXXXXXX
            return `+${digits}`;
        }
        
        if (digits.startsWith('07') || digits.startsWith('06')) {
            // Format local 07XXXXXXXX → +2417XXXXXXXX
            const operator = digits.startsWith('07') ? '7' : '6';
            if (digits.length >= 9) {
                return `+241${operator}${digits.slice(2)}`;
            }
        }
        
        if (digits.startsWith('7') || digits.startsWith('6')) {
            // Format sans le 0 : 7XXXXXXXX → +2417XXXXXXXX
            if (digits.length >= 8) {
                return `+241${digits}`;
            }
        }
        
        return value; // Autres cas : laisser tel quel pour permettre la saisie progressive
    };

    // Validation téléphone gabonais
    const validateGabonPhone = (phone: string, country: string): boolean => {
        if (country !== 'Gabon') return phone.length >= 8; // Validation basique pour autres pays
        
        const gabonPattern = /^\+241[67]\d{7}$/;
        return gabonPattern.test(phone);
    };

    // Gestion des changements
    const handleInputChange = (field: keyof AdhesionFormData, value: string) => {
        let processedValue = value;
        
        if (field === 'nip') {
            processedValue = formatNip(value);
            checkNipWithDebounce(processedValue);
        }
        
        // 🆕 FORMATAGE TÉLÉPHONE GABONAIS
        if (field === 'phone' && formData.country === 'Gabon') {
            processedValue = formatGabonPhone(value, formData.country);
        }

        if (field === 'referredBy') {
            onFormDataChange({ [field]: processedValue });
            if (onReferralCodeChange) {
                onReferralCodeChange(processedValue);
            }

            if (processedValue && !referrerInfo) {
                trackReferralValidation(processedValue, false);
            }
        } else {
            onFormDataChange({ [field]: processedValue });
        }
    };

    // Validation et soumission avec tracking d'erreurs
    const handleSubmit = () => {
        const newErrors: Record<string, string> = {};

        // Validations obligatoires (pour tous les niveaux)
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'Le prénom est obligatoire';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Le nom est obligatoire';
        }
        if (!formData.phone.trim()) {
            newErrors.phone = 'Le téléphone est obligatoire';
        } else if (!validateGabonPhone(formData.phone, formData.country)) {
            if (formData.country === 'Gabon') {
                newErrors.phone = 'Format téléphone gabonais invalide (doit commencer par 06 ou 07)';
            } else if (formData.phone.length < 8) {
                newErrors.phone = 'Numéro de téléphone trop court';
            }
        }
        if (!formData.country.trim()) {
            newErrors.country = 'Le pays est obligatoire';
        }

        // 🆕 VALIDATIONS CONDITIONNELLES SELON LE NIVEAU
        if (membershipLevel === 'full_member') {
            // Adhérent complet : Date/lieu naissance obligatoires
            if (!formData.dateOfBirth) {
                newErrors.dateOfBirth = 'La date de naissance est obligatoire pour l\'adhésion complète';
            }
            if (!formData.placeOfBirth.trim()) {
                newErrors.placeOfBirth = 'Le lieu de naissance est obligatoire pour l\'adhésion complète';
            }
        } else if (membershipLevel === 'partial_member') {
            // Membre partiel : Date de naissance OBLIGATOIRE pour la connexion
            if (!formData.dateOfBirth) {
                newErrors.dateOfBirth = 'La date de naissance est obligatoire pour vous connecter à votre espace membre';
            }
            // Lieu de naissance optionnel
        } else {
            // Sympathisant : Date/lieu optionnels
            // Pas d'erreur si non remplis
        }

        // ✅ VALIDATION SEXE OBLIGATOIRE
        if (!formData.gender.trim()) {
            newErrors.gender = 'Le sexe est obligatoire';
        }

        // ✅ VALIDATION PROFESSION OBLIGATOIRE
        if (!formData.profession.trim()) {
            newErrors.profession = 'La profession est obligatoire';
        }

        // Validation spécifique pour le Gabon
        if (formData.country === 'Gabon' && !formData.province.trim()) {
            newErrors.province = 'La province est obligatoire pour le Gabon';
        }

        // Validation ville
        if (!formData.city.trim() && formData.city !== 'autre') {
            newErrors.city = 'La ville est obligatoire';
        }
        if (formData.city === 'autre' && !formData.customCity.trim()) {
            newErrors.customCity = 'Veuillez préciser le nom de votre ville';
        }

        // 🆕 VALIDATION NIP CONDITIONNELLE
        if (membershipLevel === 'full_member') {
            // Adhérent complet : NIP OBLIGATOIRE
            if (!formData.nip.trim()) {
                newErrors.nip = 'Le N° NIP est obligatoire pour l\'adhésion complète';
            } else if (!validateNip(formData.nip)) {
                newErrors.nip = 'Format NIP invalide (XX-XXXX-XXXXXXXX)';
            } else if (nipStatus.available === false) {
                newErrors.nip = nipStatus.message || 'Ce NIP est déjà utilisé';
            } else if (nipStatus.available === null && formData.nip.trim()) {
                newErrors.nip = 'Vérification du NIP en cours, veuillez patienter';
            }
        } else {
            // Sympathisant/Partiel : NIP optionnel, mais si fourni, doit être valide
            if (formData.nip.trim()) {
                if (!validateNip(formData.nip)) {
                    newErrors.nip = 'Format NIP invalide (XX-XXXX-XXXXXXXX)';
                } else if (nipStatus.available === false) {
                    newErrors.nip = nipStatus.message || 'Ce NIP est déjà utilisé';
                } else if (nipStatus.available === null) {
                    newErrors.nip = 'Vérification du NIP en cours, veuillez patienter';
                }
            }
        }

        // Validation âge
        const age = calculateAge(formData.dateOfBirth);
        if (age !== null && age < 16) {
            newErrors.dateOfBirth = `Âge minimum 16 ans (vous avez ${age} ans)`;
        }

        // Validation email si fourni
        if (formData.email && formData.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = 'Format d\'email invalide';
            }
        }

        if (nipStatus.available === false || nipStatus.checking) {
            newErrors.nip = nipStatus.checking ? 'Vérification en cours...' : nipStatus.message;
        }

        setValidationErrors(newErrors);

        // Tracking des erreurs de validation
        if (Object.keys(newErrors).length > 0) {
            Object.entries(newErrors).forEach(([field, error]) => {
                trackValidationError('personal_info', field, error);
            });

            trackAdhesionStep(1, 'personal_info', 'error');


            const firstErrorField = Object.keys(newErrors)[0];
            const errorElement = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                errorElement.focus();
            }
        } else {
            trackAdhesionStep(1, 'personal_info', 'complete');
            onNext();
        }
    };

    const getFieldError = (fieldName: string): string | undefined => {
        return validationErrors[fieldName] || errors[fieldName];
    };

    const hasFieldError = (fieldName: string): boolean => {
        return Boolean(getFieldError(fieldName));
    };

    const age = calculateAge(formData.dateOfBirth);

    return (
        <div className="bg-white p-8 rounded-lg shadow-xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Informations personnelles</h2>

            {Object.keys(validationErrors).length > 0 && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                    <div className="flex">
                        <AlertCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="text-red-800 font-medium">Veuillez corriger les erreurs suivantes :</h3>
                            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                                {Object.entries(validationErrors).map(([field, error]) => (
                                    <li key={field}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-6">
                {/* Nom et Prénom */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                                hasFieldError('firstName')
                                    ? 'border-red-300 focus:ring-red-600 bg-red-50'
                                    : 'border-gray-300 focus:ring-blue-600'
                            }`}
                            placeholder="Votre prénom"
                        />
                        {getFieldError('firstName') && (
                            <p className="text-red-600 text-sm mt-1 flex items-center">
                                <X className="w-4 h-4 mr-1"/>
                                {getFieldError('firstName')}
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                                hasFieldError('lastName')
                                    ? 'border-red-300 focus:ring-red-600 bg-red-50'
                                    : 'border-gray-300 focus:ring-blue-600'
                            }`}
                            placeholder="Votre nom"
                        />
                        {getFieldError('lastName') && (
                            <p className="text-red-600 text-sm mt-1 flex items-center">
                                <X className="w-4 h-4 mr-1"/>
                                {getFieldError('lastName')}
                            </p>
                        )}
                    </div>
                </div>

                {/* Téléphone et Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Téléphone *
                            {formData.country === 'Gabon' && (
                                <span className="text-xs text-blue-600 ml-1">
                                    (format automatique +241)
                                </span>
                            )}
                        </label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                                hasFieldError('phone')
                                    ? 'border-red-300 focus:ring-red-600 bg-red-50'
                                    : formData.country === 'Gabon' && formData.phone && validateGabonPhone(formData.phone, formData.country)
                                        ? 'border-green-300 focus:ring-green-600 bg-green-50' // ✅ Valide
                                        : 'border-gray-300 focus:ring-blue-600'
                            }`}
                            placeholder={formData.country === 'Gabon' ? "07 XX XX XX XX" : "+XXX XXXXXXXXX"}
                        />
                        {getFieldError('phone') ? (
                            <p className="text-red-600 text-sm mt-1 flex items-center">
                                <X className="w-4 h-4 mr-1"/>
                                {getFieldError('phone')}
                            </p>
                        ) : formData.country === 'Gabon' ? (
                            <div className="text-xs text-gray-500 mt-1">
                                <p>📱 Saisissez : 07123456 ou 06123456</p>
                                <p>✨ Devient automatiquement : +2417123456 ou +2416123456</p>
                            </div>
                        ) : (
                            <p className="text-xs text-gray-500 mt-1">
                                Format international recommandé (+XXX...)
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email (optionnel)</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                                hasFieldError('email')
                                    ? 'border-red-300 focus:ring-red-600 bg-red-50'
                                    : 'border-gray-300 focus:ring-blue-600'
                            }`}
                            placeholder="votre.email@exemple.com"
                        />
                        {getFieldError('email') && (
                            <p className="text-red-600 text-sm mt-1 flex items-center">
                                <X className="w-4 h-4 mr-1"/>
                                {getFieldError('email')}
                            </p>
                        )}
                    </div>
                </div>

                {/* Date et Lieu de naissance */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Calendar className="w-4 h-4 inline mr-1"/>
                            Date de naissance *
                        </label>
                        <div className="relative">
                            <input
                                type="date"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                                max={new Date(Date.now() - 16 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                                    hasFieldError('dateOfBirth')
                                        ? 'border-red-300 focus:ring-red-600 bg-red-50'
                                        : age && age >= 16
                                            ? 'border-green-300 focus:ring-green-600'
                                            : 'border-gray-300 focus:ring-blue-600'
                                }`}
                            />
                            {age !== null && !hasFieldError('dateOfBirth') && (
                                <span
                                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-xs px-2 py-1 rounded ${
                                        age >= 16 ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'
                                    }`}>
                                    {age} ans
                                </span>
                            )}
                        </div>
                        {getFieldError('dateOfBirth') && (
                            <p className="text-red-600 text-sm mt-1 flex items-center">
                                <X className="w-4 h-4 mr-1"/>
                                {getFieldError('dateOfBirth')}
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <MapPin className="w-4 h-4 inline mr-1"/>
                            Lieu de naissance *
                        </label>
                        <input
                            type="text"
                            name="placeOfBirth"
                            value={formData.placeOfBirth}
                            onChange={(e) => handleInputChange('placeOfBirth', e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                                hasFieldError('placeOfBirth')
                                    ? 'border-red-300 focus:ring-red-600 bg-red-50'
                                    : 'border-gray-300 focus:ring-blue-600'
                            }`}
                            placeholder="Votre ville de naissance"
                        />
                        {getFieldError('placeOfBirth') && (
                            <p className="text-red-600 text-sm mt-1 flex items-center">
                                <X className="w-4 h-4 mr-1"/>
                                {getFieldError('placeOfBirth')}
                            </p>
                        )}
                    </div>
                </div>

                {/* ✅ NOUVEAU: Sexe et Profession */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            <User className="w-4 h-4 inline mr-1"/>
                            Sexe *
                        </label>
                        <div className="inline-flex rounded-lg border border-gray-300 bg-gray-100 p-1">
                            <button
                                type="button"
                                onClick={() => handleInputChange('gender', 'Homme')}
                                className={`px-6 py-2 rounded-md font-medium text-sm transition-all ${
                                    formData.gender === 'Homme'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-700 hover:text-gray-900'
                                }`}
                            >
                                Homme
                            </button>
                            <button
                                type="button"
                                onClick={() => handleInputChange('gender', 'Femme')}
                                className={`px-6 py-2 rounded-md font-medium text-sm transition-all ${
                                    formData.gender === 'Femme'
                                        ? 'bg-pink-600 text-white shadow-sm'
                                        : 'text-gray-700 hover:text-gray-900'
                                }`}
                            >
                                Femme
                            </button>
                        </div>
                        {getFieldError('gender') && (
                            <p className="text-red-600 text-sm mt-2 flex items-center">
                                <X className="w-4 h-4 mr-1"/>
                                {getFieldError('gender')}
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Briefcase className="w-4 h-4 inline mr-1"/>
                            Profession *
                        </label>
                        <input
                            type="text"
                            name="profession"
                            value={formData.profession}
                            onChange={(e) => handleInputChange('profession', e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                                hasFieldError('profession')
                                    ? 'border-red-300 focus:ring-red-600 bg-red-50'
                                    : 'border-gray-300 focus:ring-blue-600'
                            }`}
                            placeholder="Votre profession"
                        />
                        {getFieldError('profession') && (
                            <p className="text-red-600 text-sm mt-1 flex items-center">
                                <X className="w-4 h-4 mr-1"/>
                                {getFieldError('profession')}
                            </p>
                        )}
                    </div>
                </div>

                {/* NIP */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        N° NIP {membershipLevel === 'full_member' ? '*' : '(optionnel)'}
                        <span className="text-xs text-gray-500 font-normal ml-1">(format: XX-XXXX-XXXXXXXX)</span>
                        {membershipLevel !== 'full_member' && (
                            <span className="text-blue-600 text-xs ml-1 block">
                                Ajoutez votre NIP plus tard pour votre carte officielle
                            </span>
                        )}
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            name="nip"
                            value={formData.nip}
                            onChange={(e) => handleInputChange('nip', e.target.value)}
                            maxLength={16}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent font-mono uppercase transition-all ${
                                hasFieldError('nip') || nipStatus.available === false
                                    ? 'border-red-300 focus:ring-red-600 bg-red-50'
                                    : nipStatus.available === true
                                        ? 'border-green-300 focus:ring-green-600 bg-green-50'
                                        : nipStatus.checking
                                            ? 'border-yellow-300 focus:ring-yellow-600 bg-yellow-50'
                                            : 'border-gray-300 focus:ring-blue-600'
                            }`}
                            placeholder="12-0014-99821427"
                        />

                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            {nipStatus.checking ? (
                                <div
                                    className="animate-spin rounded-full h-5 w-5 border-2 border-yellow-600 border-t-transparent"></div>
                            ) : nipStatus.available === true ? (
                                <Check className="w-5 h-5 text-green-600"/>
                            ) : nipStatus.available === false ? (
                                <X className="w-5 h-5 text-red-600"/>
                            ) : null}
                        </div>
                    </div>

                    {getFieldError('nip') ? (
                        <p className="text-red-600 text-sm mt-1 flex items-center">
                            <X className="w-4 h-4 mr-1"/>
                            {getFieldError('nip')}
                        </p>
                    ) : nipStatus.checking ? (
                        <p className="text-yellow-600 text-sm mt-1 flex items-center">
                            <div
                                className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-600 border-t-transparent mr-1"></div>
                            Vérification de l'unicité du NIP...
                        </p>
                    ) : nipStatus.available === true ? (
                        <p className="text-green-600 text-sm mt-1 flex items-center">
                            <Check className="w-4 h-4 mr-1"/>
                            Format valide
                        </p>
                    ) : nipStatus.available === false ? (
                        <p className="text-red-600 text-sm mt-1 flex items-center">
                            <X className="w-4 h-4 mr-1"/>
                            {nipStatus.message}
                        </p>
                    ) : (
                        <div className="text-gray-500 text-xs mt-1">
                            <p>Format: XX-XXXX-XXXXXXXX (lettres et chiffres acceptés)</p>
                            <p>Exemple: 12-0018-19821127</p>
                        </div>
                    )}
                </div>

                {/* LOCALISATION COMPLÈTE */}
                <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-4">
                        <MapPin className="w-5 h-5 mr-2"/>
                        Localisation
                    </h3>

                    {/* Pays avec autocomplétion */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Globe className="w-4 h-4 inline mr-1"/>
                                Pays *
                            </label>
                            <div ref={countryInputRef} className="relative">
                                <input
                                    type="text"
                                    name="country"
                                    value={countrySearch}
                                    onChange={(e) => handleCountrySearch(e.target.value)}
                                    onFocus={() => setShowCountryDropdown(true)}
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                                        hasFieldError('country')
                                            ? 'border-red-300 focus:ring-red-600 bg-red-50'
                                            : 'border-gray-300 focus:ring-blue-600'
                                    }`}
                                    placeholder="Tapez pour rechercher un pays..."
                                    required
                                />
                                {showCountryDropdown && filteredCountries.length > 0 && (
                                    <div
                                        className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        {filteredCountries.slice(0, 10).map((country) => (
                                            <button
                                                key={country.id}
                                                type="button"
                                                onClick={() => selectCountry(country)}
                                                className="w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 border-b border-gray-100 last:border-b-0 flex items-center space-x-2"
                                            >
                                                <span
                                                    className="font-medium text-gray-600 text-xs bg-gray-100 px-2 py-1 rounded">
                                                    {country.code}
                                                </span>
                                                <span>{country.name}</span>
                                            </button>
                                        ))}
                                        {filteredCountries.length > 10 && (
                                            <div className="px-4 py-2 text-sm text-gray-500 italic">
                                                Et {filteredCountries.length - 10} autres pays...
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {getFieldError('country') && (
                                <p className="text-red-600 text-sm mt-1 flex items-center">
                                    <X className="w-4 h-4 mr-1"/>
                                    {getFieldError('country')}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Province/État {formData.country === 'Gabon' ? '*' : '(optionnel)'}
                            </label>
                            {formData.country === 'Gabon' ? (
                                <select
                                    value={formData.province}
                                    onChange={(e) => handleInputChange('province', e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    required
                                >
                                    <option value="">Choisir une province</option>
                                    {filteredProvinces.map(province => (
                                        <option key={province.id} value={province.name}>{province.name}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    value={formData.province}
                                    onChange={(e) => handleInputChange('province', e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    placeholder="État/Province/Région"
                                />
                            )}
                            {errors.province && <p className="text-red-600 text-xs mt-1">{errors.province}</p>}
                        </div>
                    </div>

                    {/* Département et Ville */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Département (optionnel)
                            </label>
                            {formData.country === 'Gabon' ? (
                                <select
                                    value={formData.department}
                                    onChange={(e) => handleInputChange('department', e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    disabled={!formData.province}
                                >
                                    <option value="">Choisir un département</option>
                                    {filteredDepartments.map(department => (
                                        <option key={department.id} value={department.name}>
                                            {department.name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    value={formData.department}
                                    onChange={(e) => handleInputChange('department', e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    placeholder="Département/Comté"
                                />
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Ville *
                                {formData.country === 'Gabon' && (formData.department || formData.province) && (
                                    <span className="text-xs text-gray-500 ml-1">
                                        ({filteredCities.length} disponibles)
                                    </span>
                                )}
                            </label>
                            {formData.country === 'Gabon' ? (
                                <select
                                    value={formData.city}
                                    onChange={(e) => handleInputChange('city', e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    required
                                    disabled={!formData.province}
                                >
                                    <option value="">Choisir une ville</option>
                                    {filteredCities.map(city => (
                                        <option key={city.id} value={city.name}>{city.name}</option>
                                    ))}
                                    <option value="autre">Autre (préciser ci-dessous)</option>
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    value={formData.city}
                                    onChange={(e) => handleInputChange('city', e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    placeholder="Votre ville"
                                    required
                                />
                            )}
                            {errors.city && <p className="text-red-600 text-xs mt-1">{errors.city}</p>}
                        </div>
                    </div>

                    {/* Ville personnalisée */}
                    {formData.country === 'Gabon' && formData.city === 'autre' && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Précisez votre ville *</label>
                            <input
                                type="text"
                                value={formData.customCity}
                                onChange={(e) => handleInputChange('customCity', e.target.value)}
                                placeholder="Nom de votre ville"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                            />
                            {errors.customCity && <p className="text-red-600 text-xs mt-1">{errors.customCity}</p>}
                        </div>
                    )}

                    {/* Informations sur le département sélectionné */}
                    {formData.country === 'Gabon' && formData.department && (
                        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-center space-x-2">
                                <MapPin className="w-4 h-4 text-green-600"/>
                                <div>
                                    <p className="text-green-800 font-medium text-sm">
                                        Département: {formData.department}
                                    </p>
                                    <p className="text-green-700 text-xs">
                                        Chef-lieu: {getDepartmentInfo(formData.department)?.chef_lieu}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Message adhésion internationale */}
                    {formData.country !== 'Gabon' && (
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-start space-x-2">
                                <Globe className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"/>
                                <div>
                                    <p className="text-blue-800 font-medium text-sm">
                                        Adhésion internationale
                                    </p>
                                    <p className="text-blue-700 text-xs mt-1">
                                        Vous rejoignez l'UDB depuis {formData.country}. Votre adhésion sera traitée par
                                        notre équipe internationale.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Code de parrainage */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Code de parrainage (optionnel)</label>
                    <input
                        type="text"
                        value={formData.referredBy}
                        onChange={(e) => handleInputChange('referredBy', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                            referrerInfo && formData.referredBy
                                ? 'border-green-300 focus:ring-green-600 bg-green-50'
                                : 'border-gray-300 focus:ring-blue-600'
                        }`}
                        placeholder="UDB-XXXXX"
                    />
                    {referrerInfo && formData.referredBy && (
                        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center space-x-2 mb-1">
                                <Check className="w-4 h-4 text-green-600"/>
                                <span className="text-green-800 font-medium text-sm">Code valide !</span>
                            </div>
                            <p className="text-green-700 text-sm">
                                <strong>Parrain :</strong> {referrerInfo.firstName} {referrerInfo.lastName}
                            </p>
                            <p className="text-green-600 text-xs">
                                📍 {referrerInfo.city}, {referrerInfo.province}
                            </p>
                        </div>
                    )}
                </div>

                <button
                    onClick={handleSubmit}
                    className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-4 rounded-lg font-semibold hover:shadow-lg hover:from-blue-700 hover:to-green-700 transition-all flex items-center justify-center space-x-2"
                >
                    <span>Continuer</span>
                    <Check className="w-5 h-5"/>
                </button>
            </div>
        </div>
    );
};
export default PersonalInfoForm;// components/PersonalInfoForm.tsx - Version avec sexe et profession obligatoire
